import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { ThemeProvider } from '@/context/ThemeContext'
import { AuthProvider } from '@/context/AuthContext'
import { Layout } from '@/components/layout/Layout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { ConsentBanner } from '@/components/ConsentBanner'
import { Login } from '@/pages/Login'
import { Register } from '@/pages/Register'
import { ForgotPassword } from '@/pages/ForgotPassword'
import { ResetPassword } from '@/pages/ResetPassword'
import { Overview } from '@/pages/Overview'
import { Domains } from '@/pages/Domains'
import { DomainDetail } from '@/pages/DomainDetail'
import { Mailboxes } from '@/pages/Mailboxes'
import { MailboxDetail } from '@/pages/MailboxDetail'
import { ApiKeys } from '@/pages/ApiKeys'
import { Webhooks } from '@/pages/Webhooks'
import { Templates } from '@/pages/Templates'
import { ScheduledEmails } from '@/pages/ScheduledEmails'
import { SendHistory } from '@/pages/SendHistory'
import { Inbox } from '@/pages/Inbox'
import { Webmail } from '@/pages/Webmail'
import { Billing } from '@/pages/Billing'
import { Settings } from '@/pages/Settings'
import { Deliverability } from '@/pages/Deliverability'
import { DeliverabilityDetail } from '@/pages/DeliverabilityDetail'
import { Aliases } from '@/pages/Aliases'
import { Contacts } from '@/pages/Contacts'
import { ImportGuide } from '@/pages/ImportGuide'
import { MigrationCenter } from '@/pages/MigrationCenter'
import { SetupMailbox } from '@/pages/SetupMailbox'
import { ClientSetup } from '@/pages/ClientSetup'
import { VerifyEmail } from '@/pages/VerifyEmail'
import { PublicProfile } from '@/pages/PublicProfile'
import { AccountClosure } from '@/pages/AccountClosure'

function SubscriptionRequiredNotice() {
  const navigate = useNavigate()

  useEffect(() => {
    const handleSubscriptionRequired = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string; actionUrl?: string }>).detail
      toast.error(detail.message || 'Choose a plan to continue.', {
        action: {
          label: 'View plans',
          onClick: () => navigate(detail.actionUrl || '/billing'),
        },
      })
    }

    window.addEventListener('subscription-required', handleSubscriptionRequired)
    return () => window.removeEventListener('subscription-required', handleSubscriptionRequired)
  }, [navigate])

  return null
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionRequiredNotice />
        <ErrorBoundary>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/setup" element={<SetupMailbox />} />
              <Route path="/client-setup" element={<ClientSetup />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Route>
            <Route path="/account-closure" element={<ProtectedRoute><AccountClosure /></ProtectedRoute>} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<Overview />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/domains/:id" element={<DomainDetail />} />
              <Route path="/mailboxes" element={<Mailboxes />} />
              <Route path="/mailboxes/:id" element={<MailboxDetail />} />
              <Route path="/api-keys" element={<ApiKeys />} />
              <Route path="/webhooks" element={<Webhooks />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/scheduled" element={<ScheduledEmails />} />
              <Route path="/sending" element={<SendHistory />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/webmail" element={<Webmail />} />
              <Route path="/exports" element={<Navigate to="/settings?tab=export" replace />} />
              <Route path="/deliverability" element={<Deliverability />} />
              <Route path="/deliverability/domains/:id" element={<DeliverabilityDetail />} />
              <Route path="/aliases" element={<Aliases />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/imports" element={<MigrationCenter />} />
              <Route path="/imports/guide" element={<ImportGuide />} />
              <Route path="/migration" element={<MigrationCenter />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="/u/:accountId" element={<PublicProfile />} />
            <Route path="*" element={<div className="flex items-center justify-center min-h-screen text-muted-foreground">404 — Page not found</div>} />
          </Routes>
          <ConsentBanner />
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
